/** Pick an image file and import it into the project's assets (ribbon / Mod-Shift-K). */
import {
  assetNameFromSource,
  assetRelativePath,
  assetStorageRoot,
} from "../lib/projectAssets";
import { isJust, just, nothing, toMaybe, type Maybe } from "../lib/maybe";
import { importProjectAsset, pickImageFile } from "../lib/tauri/assets";
import { isTauri } from "../lib/runtime";
import type { EditorKind } from "../lib/tauri/core";

export type ImageInsertContext = {
  mode: EditorKind;
  rootPath: Maybe<string>;
  activePath: Maybe<string>;
};

/**
 * Pick an image file, copy it into the project's Assets/ and
 * return the document-relative `src`. `nothing` when cancelled, unsupported
 * (web, no project) or the copy failed (already reported to the user).
 */
export async function pickAndImportImage(
  context: ImageInsertContext,
): Promise<Maybe<string>> {
  const storageRoot = assetStorageRoot(
    context.mode,
    context.rootPath,
    context.activePath,
  );
  if (isJust(storageRoot) === false || isTauri() === false) return nothing;

  const source = toMaybe(await pickImageFile());
  if (isJust(source) === false) return nothing;

  const assetName = assetNameFromSource(source.value);
  try {
    return just(
      assetRelativePath(
        await importProjectAsset(storageRoot.value, source.value, assetName),
      ),
    );
  } catch (error) {
    console.error("Failed to import image", error);
    window.alert(
      error instanceof Error ? error.message : "Could not add image.",
    );
    return nothing;
  }
}
